import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Copy, Download, Upload } from "lucide-react";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [wedding, setWedding] = useState(null);
  const [bende, setBende] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    api.get("/admin/verify").then(() => loadAll()).catch(() => logout());
  }, [token]);

  const loadAll = () => {
    api.get("/wedding").then((r) => setWedding(r.data));
    api.get("/bende").then((r) => setBende(r.data));
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/login", { password });
      localStorage.setItem("admin_token", r.data.token);
      setToken(r.data.token);
      toast.success("Ingelogd");
    } catch { toast.error("Verkeerd wachtwoord"); }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
  };

  const saveWedding = async (e) => {
    e.preventDefault();
    try {
      await api.put("/wedding", wedding);
      toast.success("Bewaard");
    } catch { toast.error("Mislukt"); }
  };

  const copyLink = (path) => {
    const url = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(url);
    toast.success("Link gekopieerd!");
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `lien-gust-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Export gedownload");
    } catch { toast.error("Export mislukt"); }
    finally { setExporting(false); }
  };

  const importData = async (file) => {
    if (!file) return;
    if (!window.confirm("Weet je zeker dat je wil importeren? Dit VERVANGT alle huidige data (bende, wensen, foto's).")) {
      importRef.current.value = "";
      return;
    }
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const r = await api.post("/admin/import", data);
      toast.success(`Geïmporteerd: ${Object.entries(r.data.imported).map(([k, v]) => `${v} ${k}`).join(", ")}`);
      loadAll();
    } catch (e) {
      toast.error("Import mislukt — controleer het bestand");
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-5 py-20" data-testid="admin-login-page">
        <h1 className="font-script text-5xl text-center text-[#006A71] mb-2">Admin</h1>
        <p className="text-[10px] tracking-[0.3em] uppercase text-center text-[#006A71]">Alleen voor de organisator</p>
        <form onSubmit={login} className="mt-8 bg-white border border-[#006A71]/15 rounded-sm p-8 brutal-shadow">
          <label className="block">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Wachtwoord</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="scrap-input mt-1" data-testid="admin-password-input" />
          </label>
          <button className="btn-primary mt-6 w-full justify-center" type="submit" data-testid="admin-login-btn">
            Inloggen
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="admin-page">
      <div className="flex justify-between items-center mb-12">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Amministrazione</p>
          <h1 className="font-script text-5xl text-[#006A71]">Admin Paneel</h1>
        </div>
        <button onClick={logout} className="btn-ghost" data-testid="admin-logout-btn">
          <LogOut className="w-3.5 h-3.5 inline mr-2" /> Uitloggen
        </button>
      </div>

      {/* SHARE LINKS */}
      <section className="bg-white border border-[#006A71]/15 rounded-sm p-8 brutal-shadow mb-8">
        <h2 className="font-script text-3xl text-[#006A71] mb-3">Geheime links voor vrienden</h2>
        <p className="text-sm text-[#4A6165] mb-6 font-serif italic">
          Deel deze links met de vrienden zodat zij wensen en foto's kunnen toevoegen. <strong className="not-italic">Niet doorsturen naar Lien & Gust!</strong>
        </p>
        <div className="space-y-3">
          <LinkRow path="/voor-vrienden/wens" label="Wens schrijven" onCopy={copyLink} />
        </div>
      </section>

      {/* BACKUP: EXPORT / IMPORT */}
      <section className="bg-white border border-[#006A71]/15 rounded-sm p-8 brutal-shadow mb-8">
        <h2 className="font-script text-3xl text-[#006A71] mb-3">Backup: export & import</h2>
        <p className="text-sm text-[#4A6165] mb-6 font-serif italic">
          Bewaar een backup van alle data (bruidspaar, bende, wensen, foto's) als JSON-bestand, of zet een eerdere backup terug. <strong className="not-italic">Let op:</strong> importeren vervangt alle huidige data.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button" onClick={exportData} disabled={exporting}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="admin-export-btn"
          >
            <Download className="w-3.5 h-3.5" />
            {exporting ? "Exporteren..." : "Exporteer alles"}
          </button>
          <button
            type="button" onClick={() => importRef.current?.click()} disabled={importing}
            className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="admin-import-btn"
          >
            <Upload className="w-3.5 h-3.5" />
            {importing ? "Importeren..." : "Importeer backup"}
          </button>
          <input
            ref={importRef} type="file" accept="application/json,.json" className="hidden"
            onChange={(e) => importData(e.target.files?.[0])}
          />
        </div>
      </section>

      {/* BENDE EDIT LINKS */}
      <section className="bg-white border border-[#006A71]/15 rounded-sm p-8 brutal-shadow mb-8">
        <h2 className="font-script text-3xl text-[#006A71] mb-3">Bende: links & onthulling</h2>
        <p className="text-sm text-[#4A6165] mb-6 font-serif italic">
          Deze bewerk-links werken alleen als je ingelogd bent als admin. Stel eventueel een <strong className="not-italic">onthullingsdatum</strong> in — vóór die datum toont de boodschap-pagina alleen een "binnenkort beschikbaar"-melding.
        </p>
        <p className="text-xs text-[#4A6165] mb-4 font-serif italic">
          Domi & Bas krijgt automatisch de lokale PowerPoint-link naar de presentatie.
        </p>
        <div className="space-y-3">
          {bende.map((m) => (
            <BendeRow key={m.slug} member={m} onCopy={copyLink} onReload={loadAll} />
          ))}
        </div>
      </section>

      {/* WEDDING INFO */}
      {wedding && (
        <form onSubmit={saveWedding} className="bg-white border border-[#006A71]/15 rounded-sm p-8 brutal-shadow mb-8">
          <h2 className="font-script text-3xl text-[#006A71] mb-6">Bruidspaar & data</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Bruid" value={wedding.bride} onChange={(v) => setWedding({ ...wedding, bride: v })} testid="admin-bride" />
            <Field label="Bruidegom" value={wedding.groom} onChange={(v) => setWedding({ ...wedding, groom: v })} testid="admin-groom" />
            <Field label="Trouwdatum (YYYY-MM-DD)" value={wedding.wedding_date} onChange={(v) => setWedding({ ...wedding, wedding_date: v })} testid="admin-wedding-date" />
            <Field label="Feestdatum (YYYY-MM-DD)" value={wedding.party_date} onChange={(v) => setWedding({ ...wedding, party_date: v })} testid="admin-party-date" />
            <Field label="Feestlocatie" value={wedding.party_location} onChange={(v) => setWedding({ ...wedding, party_location: v })} testid="admin-party-location" />
              <label className="block sm:col-span-2">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Publieke pagina's</span>
                <p className="text-xs text-[#4A6165] mt-1 font-serif italic">
                  Schakel dit uit om wensen schrijven en de bewerkpagina's van de bende te blokkeren voor niet-admins.
                </p>
                <label className="mt-3 inline-flex items-center gap-3 text-sm text-[#1F3E42]">
                  <input
                    type="checkbox"
                    checked={wedding.public_pages_enabled !== false}
                    onChange={(e) => setWedding({ ...wedding, public_pages_enabled: e.target.checked })}
                    className="h-4 w-4 accent-[#006A71]"
                    data-testid="admin-public-pages-toggle"
                  />
                  Publieke pagina's toestaan
                </label>
              </label>
          </div>
          <label className="block mt-6">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Welkomsttekst</span>
            <textarea rows={3} value={wedding.intro_text || ""} onChange={(e) => setWedding({ ...wedding, intro_text: e.target.value })}
              className="scrap-input mt-1" data-testid="admin-intro-text" />
          </label>
          <button type="submit" className="btn-primary mt-8" data-testid="admin-save-wedding">Opslaan</button>
        </form>
      )}

      <p className="text-center text-[10px] tracking-[0.3em] uppercase text-[#4A6165]">
        Tip: ga naar <a href="/wensen" className="underline">/wensen</a> om bijdragen te beheren (verwijder).
      </p>
    </div>
  );
}

function LinkRow({ path, label, onCopy }) {
  const url = `${window.location.origin}${path}`;
  return (
    <div className="flex items-center gap-3 border border-[#006A71]/20 rounded-sm p-4">
      <div className="flex-1 min-w-0">
        <p className="font-serif text-lg text-[#1F3E42]">{label}</p>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A6165] truncate mt-1">{url}</p>
      </div>
      <button onClick={() => onCopy(path)} className="btn-ghost shrink-0" data-testid={`copy-link-${path}`}>
        <Copy className="w-3.5 h-3.5 inline mr-1" /> Kopieer
      </button>
    </div>
  );
}

function BendeRow({ member, onCopy, onReload }) {
  const path = `/de-bende/${member.slug}/bewerken`;
  const url = `${window.location.origin}${path}`;
  const [date, setDate] = useState(member.reveal_date ? member.reveal_date.slice(0, 10) : "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/bende/${member.slug}`, { reveal_date: date || "" });
      toast.success(date ? `Onthullingsdatum voor ${member.names} ingesteld` : `Onthullingsdatum voor ${member.names} verwijderd`);
      onReload();
    } catch { toast.error("Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-[#006A71]/20 rounded-sm p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-serif text-lg text-[#1F3E42]">{member.names}</p>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A6165] truncate mt-1">{url}</p>
        </div>
        <button onClick={() => onCopy(path)} className="btn-ghost shrink-0" data-testid={`copy-link-${path}`}>
          <Copy className="w-3.5 h-3.5 inline mr-1" /> Bewerk-link
        </button>
      </div>
      <div className="flex items-center gap-3 pt-2 border-t border-[#1F3E42]/10">
        <label className="flex-1">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Onthul vanaf</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="scrap-input mt-1"
            data-testid={`reveal-date-${member.slug}`}
          />
        </label>
        <button onClick={save} disabled={saving} className="btn-ghost shrink-0 self-end disabled:opacity-50" data-testid={`save-reveal-${member.slug}`}>
          {saving ? "..." : (member.reveal_date ? "Bewaar" : "Stel in")}
        </button>
        {member.reveal_date && (
          <span className={`text-[10px] tracking-[0.2em] uppercase shrink-0 self-end pb-2 ${member.unlocked ? "text-[#8FB9AA]" : "text-[#C8845E]"}`}>
            {member.unlocked ? "Zichtbaar" : "Verborgen"}
          </span>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="scrap-input mt-1" data-testid={testid} />
    </label>
  );
}
